import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Tabs,
  Checkbox,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  TeamOutlined,
  CrownOutlined,
  SafetyCertificateOutlined,
  KeyOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

interface User {
  id: string;
  username: string;
  email: string;
  name: string;
  role: string;
  status: 'active' | 'inactive' | 'suspended';
  last_login: string;
  created_at: string;
  tenant_id: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  level: number;
}

interface Props {
  data: any;
  onRefresh: () => void;
}

const UserRoleManagement: React.FC<Props> = ({ data, onRefresh }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [userForm] = Form.useForm();
  const [roleForm] = Form.useForm();

  // Predefined permissions
  const allPermissions = [
    'view_dashboard',
    'manage_employees',
    'view_employees',
    'manage_leave',
    'approve_leave',
    'manage_recruitment',
    'manage_training',
    'manage_performance',
    'view_reports',
    'manage_documents',
    'manage_payroll',
    'manage_users',
    'manage_roles',
    'manage_settings',
    'manage_subscriptions',
    'manage_organizations'
  ];

  // Predefined roles with RBAC levels
  const defaultRoles: Role[] = [
    {
      id: 'employee',
      name: 'Employee',
      description: 'Basic employee access - view own information only',
      level: 1,
      permissions: ['view_dashboard', 'view_employees']
    },
    {
      id: 'hr_manager',
      name: 'HR Manager',
      description: 'HR management access - manage all employee data',
      level: 2,
      permissions: [
        'view_dashboard', 'manage_employees', 'view_employees',
        'manage_leave', 'approve_leave', 'manage_recruitment',
        'manage_training', 'manage_performance', 'view_reports',
        'manage_documents'
      ]
    },
    {
      id: 'administrator',
      name: 'Administrator',
      description: 'Company IT - app administration but not HR data',
      level: 3,
      permissions: [
        'view_dashboard', 'view_employees', 'manage_users',
        'manage_roles', 'manage_settings', 'view_reports'
      ]
    },
    {
      id: 'super_admin',
      name: 'Super Administrator',
      description: 'HR Consultants - multi-organization management',
      level: 4,
      permissions: allPermissions
    },
    {
      id: 'system_admin',
      name: 'System Administrator',
      description: 'Full system access - platform management',
      level: 5,
      permissions: allPermissions
    }
  ];

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Mock data for now
      const mockUsers: User[] = [
        {
          id: '1',
          username: 'admin',
          email: 'admin@system.com',
          name: 'System Administrator',
          role: 'system_admin',
          status: 'active',
          last_login: '2025-11-07T10:00:00Z',
          created_at: '2025-01-01T00:00:00Z',
          tenant_id: 'superadmin'
        }
      ];
      setUsers(mockUsers);
      setRoles(defaultRoles);
    } catch (error) {
      message.error('Failed to load users and roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = () => {
    setEditingUser(null);
    userForm.resetFields();
    setUserModalVisible(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    userForm.setFieldsValue(user);
    setUserModalVisible(true);
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      message.error('Failed to delete user');
    }
  };

  const handleUserSubmit = async (values: any) => {
    try {
      if (editingUser) {
        message.success('User updated successfully');
      } else {
        message.success('User created successfully');
      }
      setUserModalVisible(false);
      fetchUsers();
      onRefresh();
    } catch (error) {
      message.error('Failed to save user');
    }
  };

  const handleAddRole = () => {
    setEditingRole(null);
    roleForm.resetFields();
    setRoleModalVisible(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    roleForm.setFieldsValue(role);
    setRoleModalVisible(true);
  };

  const handleRoleSubmit = async (values: any) => {
    try {
      if (editingRole) {
        message.success('Role updated successfully');
      } else {
        message.success('Role created successfully');
      }
      setRoleModalVisible(false);
      fetchUsers();
      onRefresh();
    } catch (error) {
      message.error('Failed to save role');
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'green',
      inactive: 'orange',
      suspended: 'red'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getRoleColor = (roleId: string) => {
    const colors = {
      employee: 'blue',
      hr_manager: 'green',
      administrator: 'orange',
      super_admin: 'purple',
      system_admin: 'red'
    };
    return colors[roleId as keyof typeof colors] || 'default';
  };

  const getRoleIcon = (roleId: string) => {
    const icons = {
      employee: <UserOutlined />,
      hr_manager: <TeamOutlined />,
      administrator: <SafetyCertificateOutlined />,
      super_admin: <CrownOutlined />,
      system_admin: <KeyOutlined />
    };
    return icons[roleId as keyof typeof icons] || <UserOutlined />;
  };

  const userColumns = [
    {
      title: 'User',
      key: 'user',
      render: (record: User) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.username} • {record.email}
          </div>
        </div>
      )
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (roleId: string) => {
        const role = roles.find(r => r.id === roleId);
        return (
          <Tag color={getRoleColor(roleId)} icon={getRoleIcon(roleId)}>
            {role?.name || roleId}
          </Tag>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : 'Never'
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: User) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditUser(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const roleColumns = [
    {
      title: 'Role',
      key: 'role',
      render: (record: Role) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {getRoleIcon(record.id)} {record.name}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Level {record.level} • {record.description}
          </div>
        </div>
      )
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions: string[]) => (
        <div>
          <Text strong>{permissions.length}</Text> permissions
          <div style={{ marginTop: '4px' }}>
            {permissions.slice(0, 3).map(perm => (
              <Tag key={perm}>{perm}</Tag>
            ))}
            {permissions.length > 3 && (
              <Tag>+{permissions.length - 3} more</Tag>
            )}
          </div>
        </div>
      )
    },
    {
      title: 'Users',
      key: 'user_count',
      render: (record: Role) => {
        const userCount = users.filter(u => u.role === record.id).length;
        return <Text>{userCount} users</Text>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Role) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditRole(record)}
          />
        </Space>
      )
    }
  ];

  const stats = {
    totalUsers: users.length,
    activeUsers: users.filter(u => u.status === 'active').length,
    totalRoles: roles.length,
    adminUsers: users.filter(u => ['administrator', 'super_admin', 'system_admin'].includes(u.role)).length
  };

  return (
    <div>
      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={stats.totalUsers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Users"
              value={stats.activeUsers}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Roles"
              value={stats.totalRoles}
              prefix={<SafetyCertificateOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Admin Users"
              value={stats.adminUsers}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Users and Roles Tabs */}
      <Card>
        <Tabs defaultActiveKey="users">
          <TabPane tab="Users" key="users">
            <div style={{ marginBottom: '16px' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddUser}
              >
                Add User
              </Button>
            </div>
            <Table
              columns={userColumns}
              dataSource={users}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} users`,
              }}
            />
          </TabPane>

          <TabPane tab="Roles & Permissions" key="roles">
            <div style={{ marginBottom: '16px' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddRole}
              >
                Create Custom Role
              </Button>
            </div>
            <Table
              columns={roleColumns}
              dataSource={roles}
              rowKey="id"
              loading={loading}
              pagination={false}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* User Modal */}
      <Modal
        title={editingUser ? 'Edit User' : 'Add New User'}
        open={userModalVisible}
        onCancel={() => setUserModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={handleUserSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter full name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="username"
                label="Username"
                rules={[{ required: true, message: 'Please enter username' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' }
            ]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label="Role"
                rules={[{ required: true, message: 'Please select role' }]}
              >
                <Select>
                  {roles.map(role => (
                    <Option key={role.id} value={role.id}>
                      {getRoleIcon(role.id)} {role.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="suspended">Suspended</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter password' }]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUser ? 'Update' : 'Create'} User
              </Button>
              <Button onClick={() => setUserModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Role Modal */}
      <Modal
        title={editingRole ? 'Edit Role' : 'Create Custom Role'}
        open={roleModalVisible}
        onCancel={() => setRoleModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form
          form={roleForm}
          layout="vertical"
          onFinish={handleRoleSubmit}
        >
          <Form.Item
            name="name"
            label="Role Name"
            rules={[{ required: true, message: 'Please enter role name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            name="level"
            label="Access Level"
            rules={[{ required: true, message: 'Please select access level' }]}
          >
            <Select>
              <Option value={1}>Level 1 - Employee</Option>
              <Option value={2}>Level 2 - HR Manager</Option>
              <Option value={3}>Level 3 - Administrator</Option>
              <Option value={4}>Level 4 - Super Administrator</Option>
              <Option value={5}>Level 5 - System Administrator</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="permissions"
            label="Permissions"
            rules={[{ required: true, message: 'Please select permissions' }]}
          >
            <Checkbox.Group>
              <Row gutter={[16, 16]}>
                {allPermissions.map(permission => (
                  <Col span={12} key={permission}>
                    <Checkbox value={permission}>
                      {permission.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRole ? 'Update' : 'Create'} Role
              </Button>
              <Button onClick={() => setRoleModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserRoleManagement;
